import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { KPICard, EmptyState } from "@/components/DashboardShell";
import ActionPanel from "@/components/ActionPanel";
import { Wallet, ChartBar, Money, Truck, MapPin, ClipboardText } from "@phosphor-icons/react";
import { formatPrice, UNITS_LABEL } from "@/lib/format";

/**
 * Real, actionable versions of the transporter dashboard subpages. They pull
 * live buyer-request data (real loads) from /buyer-requests and surface each
 * one with a WhatsApp jump so a transportista can bid immediately.
 */
export function TransportistaOfertasPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/buyer-requests", { params: { limit: 30 } })
            .then(({ data }) => setItems(data.items || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);
    return (
        <>
            <div className="mb-6">
                <div className="eyebrow mb-1">Cargas por cotizar</div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Solicitudes de compradores</h1>
                <p className="text-muted-foreground mt-1">Cargas publicadas por compradores que necesitan transporte — envíe una oferta.</p>
            </div>
            {loading ? (
                <div className="text-center text-muted-foreground py-20">Cargando…</div>
            ) : items.length === 0 ? (
                <EmptyState
                    title="No hay solicitudes abiertas ahora mismo"
                    hint="Vuelva más tarde o consulte el listado completo del marketplace."
                    cta="Ver cargas disponibles"
                    ctaTo="/transportista/cargas"
                    icon={Truck}
                />
            ) : (
                <ul className="bg-white border border-border rounded-2xl divide-y divide-border">
                    {items.map((r) => (
                        <li key={r.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold truncate">{r.title}</div>
                                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                                    <span>{r.quantity} {UNITS_LABEL[r.unit] || r.unit}</span>
                                    <span className="inline-flex items-center gap-1"><MapPin size={12} /> {r.city}</span>
                                    {r.max_price && <span>hasta {formatPrice(r.max_price)}</span>}
                                </div>
                            </div>
                            <Link to="/solicitudes" className="text-xs font-bold text-[#2E7D32] hover:underline">
                                Cotizar →
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}

/**
 * Cargas asignadas / En tránsito / Entregadas — the transporter lifecycle
 * doesn't yet have a first-class model, so each page redirects to concrete
 * working workflows and a WhatsApp fallback for coordination.
 */
export function TransportistaAsignadasPage() {
    return (
        <>
            <div className="mb-6">
                <div className="eyebrow mb-1">Asignaciones</div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Cargas asignadas</h1>
                <p className="text-muted-foreground mt-1">Cargas confirmadas con compradores.</p>
            </div>
            <ActionPanel
                title="Sus cargas activas están un paso más adelante"
                body="Coordine directamente con el comprador por WhatsApp cuando acepte una carga. Cuando reciba una asignación en línea, aparecerá listada aquí."
                testId="transportista-asignadas"
                actions={[
                    { label: "Ver cargas disponibles", to: "/transportista/cargas", primary: true, icon: Truck },
                    { label: "Registrar vehículos", to: "/transportista/vehiculos", icon: ClipboardText },
                ]}
            />
        </>
    );
}

export function TransportistaEnTransitoPage() {
    return (
        <>
            <div className="mb-6">
                <div className="eyebrow mb-1">En tránsito</div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Fletes en camino</h1>
                <p className="text-muted-foreground mt-1">Actualice el estado de la carga en tiempo real.</p>
            </div>
            <ActionPanel
                title="Comunique el avance por WhatsApp"
                body="El seguimiento en línea llegará con el flujo de asignación completo. Mientras tanto, mantenga informado al comprador con mensajes directos."
                testId="transportista-transito"
                actions={[
                    { label: "Ver cargas disponibles", to: "/transportista/cargas", primary: true, icon: Truck },
                ]}
            />
        </>
    );
}

export function TransportistaEntregadasPage() {
    return (
        <>
            <div className="mb-6">
                <div className="eyebrow mb-1">Historial</div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Entregas completadas</h1>
                <p className="text-muted-foreground mt-1">Cargas entregadas y comentarios recibidos.</p>
            </div>
            <ActionPanel
                title="Su historial se construye con cada carga"
                body="Cuando complete una entrega en el sistema, quedará registrada aquí con la calificación del comprador."
                testId="transportista-entregadas"
                actions={[
                    { label: "Ver mis reseñas", to: "/transportista/resenas", primary: true, icon: ChartBar },
                    { label: "Cargas disponibles", to: "/transportista/cargas", icon: Truck },
                ]}
            />
        </>
    );
}

export function TransportistaGananciasPage() {
    const [k, setK] = useState({});
    useEffect(() => {
        api.get("/transportista/dashboard").then(({ data }) => setK(data?.kpis || {})).catch(() => {});
    }, []);
    return (
        <>
            <div className="mb-6">
                <div className="eyebrow mb-1">Finanzas</div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Ganancias</h1>
                <p className="text-muted-foreground mt-1">Ingresos brutos, saldo y pagos por fletes.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="transportista-ganancias-kpis">
                <KPICard label="Ingresos brutos" value={formatPrice(k.ingresos_brutos_mxn ?? 0)} icon={ChartBar} />
                <KPICard label="Ganancias pendientes" value={formatPrice(k.ganancias_pendientes_mxn ?? 0)} icon={Money} />
                <KPICard label="Saldo disponible" value={formatPrice(k.saldo_disponible_mxn ?? 0)} icon={Wallet} />
                <KPICard label="Pagado" value={formatPrice(k.pagado_mxn ?? 0)} icon={Money} />
            </div>
            <ActionPanel
                title="Consulte la actividad de sus fletes"
                body="Cierre fletes desde el listado de cargas disponibles. Los pagos se coordinan directamente con el comprador tras la aceptación."
                testId="transportista-ganancias"
                actions={[
                    { label: "Ver cargas disponibles", to: "/transportista/cargas", primary: true, icon: Truck },
                    { label: "Registrar vehículos", to: "/transportista/vehiculos", icon: ClipboardText },
                ]}
            />
        </>
    );
}
